import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Home() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [upcoming, setUpcoming] = useState([]);
  const [myBalance, setMyBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, balancesRes] = await Promise.all([
          API.get('/sessions'),
          API.get('/balances'),
        ]);
        const upcomingSessions = sessionsRes.data.filter(s => s.status === 'upcoming').slice(0, 3);
        setUpcoming(upcomingSessions);
        const myBal = balancesRes.data.personalBalances.find(b => b.userId === user.id);
        setMyBalance(myBal?.netBalance || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user.id]);

  const balanceClass = myBalance > 0 ? 'balance-positive' : myBalance < 0 ? 'balance-negative' : 'balance-zero';
  const balanceLabel = myBalance > 0 ? `You're owed Rs. ${Math.abs(myBalance).toLocaleString()}` :
    myBalance < 0 ? `You owe Rs. ${Math.abs(myBalance).toLocaleString()}` : 'All settled up ✓';

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="logo">🎾 Pedal Toast</div>
        <button className="btn btn-ghost btn-sm" onClick={logout} style={{ color: 'var(--text2)', fontSize: 13 }}>Logout</button>
      </div>

      <div className="page fade-in">
        {/* Greeting */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 4 }}>Good game, </div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 28, fontWeight: 800 }}>{user.name} 👋</div>
        </div>

        {/* Balance Card */}
        <div style={{ background: 'linear-gradient(135deg, #eff6ff, #f5f3ff)', marginBottom: 24, border: '1px solid #dbeafe' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12 }}>Your Balance</div>
          {loading ? (
            <div style={{ color: 'var(--text2)' }}>Loading...</div>
          ) : (
            <>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 32, fontWeight: 800, marginBottom: 8 }}>
                {myBalance === 0 ? '—' : `Rs. ${Math.abs(myBalance).toLocaleString()}`}
              </div>
              <div className={`balance-pill ${balanceClass}`} style={{ fontSize: 13 }}>{balanceLabel}</div>
            </>
          )}
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 16 }} onClick={() => navigate('/balances')}>
            Full ledger →
          </button>
        </div>

        {/* Quick Actions */}
        <div className="section-header">
          <div className="section-title">Quick Actions</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
          <button className="card" style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border)' }} onClick={() => navigate('/sessions/create')}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>📅</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15 }}>New Session</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>Schedule a game</div>
          </button>
          <button className="card" style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--border)' }} onClick={() => navigate('/sessions')}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🏆</div>
            <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15 }}>All Sessions</div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>View history</div>
          </button>
        </div>

        {/* Upcoming Sessions */}
        <div className="section-header">
          <div className="section-title">Upcoming Games</div>
          <button className="btn btn-ghost btn-sm text-accent" onClick={() => navigate('/sessions')}>See all</button>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : upcoming.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎾</div>
            <div style={{ color: 'var(--text2)', fontSize: 14 }}>No upcoming games.<br />Schedule one!</div>
            <button className="btn btn-primary" style={{ marginTop: 16, width: 'auto', padding: '10px 24px' }} onClick={() => navigate('/sessions/create')}>
              Create Session
            </button>
          </div>
        ) : (
          upcoming.map(session => (
            <div key={session.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/sessions/${session.id}`)}>
              <div className="row-between">
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 16 }}>{session.location_name}</div>
                  <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
                    {new Date(session.session_date).toLocaleDateString('en-PK', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}{session.player_count} players
                  </div>
                </div>
                <span className="badge badge-upcoming">Upcoming</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
